import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '..';
import RoomInput from './RoomInput';

import type { RoomInputData, RoomOpenState } from './types';
import CrossSell from '../CrossSell';

interface ICounterInputMenuProps {
  roomInputData: RoomInputData[];
  roomsOpenState: RoomOpenState[];
  isOpen: boolean;
  isHome: boolean;
  hasHeaderCloseButton?: boolean;
  hasHeaderReturnButton?: boolean;
  onRoomToggle: (roomState: RoomOpenState) => void;
  onRoomChange: (newData: RoomInputData) => void;
  onRoomDelete: (data: RoomInputData) => void;
  onRoomAdd: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirm: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClose?: () => void;
  onReturn?: () => void;
}

const CounterInputMenu = ({
  roomInputData,
  roomsOpenState,
  isOpen,
  isHome,
  hasHeaderCloseButton,
  hasHeaderReturnButton,
  onRoomToggle,
  onRoomChange,
  onRoomDelete,
  onRoomAdd,
  onConfirm,
  onClose,
  onReturn,
  ...props
}: ICounterInputMenuProps) => {
  const { t } = useTranslation();
  const [deviceWidth, setDeviceWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setDeviceWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const menu = document.querySelector('.counter-input-menu-container') as HTMLDivElement;

    if (isOpen) {
      menu.classList.add('open');
    } else {
      menu.classList.remove('open');
    }

    if (isOpen && deviceWidth >= 768) {
      document.body.style.overflowY = 'hidden';
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <section className="counter-input-menu-container" {...props}>
      {deviceWidth <= 768 && (
        <>
          <header className="counter-input-menu-header">
            {hasHeaderCloseButton && (
              <button className="counter-input-menu-header-close-button" type="button" onClick={onClose}>
                <Icon className="counter-input-menu-header-close-icon" icon="close" round />
              </button>
            )}

            {hasHeaderReturnButton && (
              <>
                {!hasHeaderCloseButton && <span />}
                <button className="counter-input-menu-header-return-button" type="button" onClick={onReturn}>
                  <Icon className="counter-input-menu-header-return-icon" icon="keyboard_arrow_left" round />
                </button>
              </>
            )}
          </header>

          <h3 className="counter-input-menu-header-title">{t('counterInput.modalTitle')}</h3>
        </>
      )}

      <section className="counter-input-menu-content">
        {roomInputData.map((room, index) => (
          <RoomInput
            key={`room-input-${room.id}`}
            data={room}
            isFirstRoom={index === 0}
            isOpenDisabled={roomInputData.length <= 1}
            isOpen={roomsOpenState[index]?.isOpen}
            onToggle={onRoomToggle}
            onChange={onRoomChange}
            onDelete={onRoomDelete}
          />
        ))}
      </section>

      <section className="counter-input-buttons-wrapper">
        <button
          className="counter-input-add-room-button"
          type="button"
          disabled={roomInputData.length >= 3}
          onClick={onRoomAdd}
        >
          {t('counterInput.addRoomButton')}
        </button>

        {deviceWidth <= 768 && <CrossSell />}

        <Button className="counter-input-submit-button" onClick={onConfirm}>
          {isHome && deviceWidth <= 768 ? t('counterInput.submitButton') : t('counterInput.confirmButton')}
        </Button>
      </section>
    </section>
  );
};

export default CounterInputMenu;
